import { MODEL_NAMES } from '../constants/index';
import { sortConversationMembers } from '../utils/messaging/conversation';
import Model, { attr, belongsTo, hasMany } from '@ember-data/model';

export const MAX_PARTICIPANTS = 9;

export default class MessagingConversationModel extends Model {
  @hasMany(MODEL_NAMES.USER, { async: false, inverse: null }) users;
  @hasMany(MODEL_NAMES.MESSAGE, { async: false, inverse: 'conversation' }) messages;
  @belongsTo(MODEL_NAMES.MESSAGE, { async: false, inverse: null }) lastMessage;

  @attr('boolean', { defaultValue: false }) disabled;
  @attr('boolean', { defaultValue: false }) inactive;
  @attr('boolean', { defaultValue: false }) isCouple;
  @attr('boolean', { defaultValue: false }) isDraft;
  @attr('boolean', { defaultValue: false }) isGroup;

  @attr('string') displayName;
  @attr('string') linkExternalId; // Id for couple record if one is present in the conversation.
  @attr('string', { defaultValue: '' }) messageDraft;

  @attr('date') readAt;
  @attr('date') updatedAt;

  @attr({
    defaultValue() {
      return {};
    },
  })
  counterpartReadAt;

  // { userId: messageId } of the latest own message each counterpart has read.
  // Populated on MESSAGING_API_LATEST_VERSION (2026-05-26) when groupMessagingEnabled is on.
  // Absent/empty on MESSAGING_API_LEGACY_VERSION (2021-06-10).
  @attr({
    defaultValue() {
      return {};
    },
  })
  counterpartReadMessageIds;

  // User ids whose membership is disabled (left / removed)
  @attr({
    defaultValue() {
      return [];
    },
  })
  disabledMembers;

  get messagesByCreatedAt() {
    return this.messages?.slice()?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }

  get mostRecentOwnMessage() {
    return this.messagesByCreatedAt?.find(message => !message.isSystem && message.isOwn);
  }

  get mostRecentReadAt() {
    const sortedReadAts = Object.values(this.counterpartReadAt).sort(
      (a, b) => new Date(b) - new Date(a)
    );
    return sortedReadAts.length ? new Date(sortedReadAts[0]) : new Date(this.readAt);
  }

  get lastReadOwnMessage() {
    return this.messagesByCreatedAt?.find(
      message => message.isOwn && new Date(message.createdAt) <= this.mostRecentReadAt
    );
  }

  readersForMessage(messageId) {
    if (messageId === null || messageId === undefined) return [];

    const users = this.users ?? [];

    return Object.entries(this.counterpartReadMessageIds ?? {})
      .filter(([, readMessageId]) => String(readMessageId) === String(messageId))
      .map(([userId]) => users.find(user => String(user.id) === String(userId)))
      .filter(Boolean);
  }

  get hasMessageDraft() {
    return this.messageDraft.trim().length > 0;
  }

  get hasPreviousMembers() {
    const disabledMembers = this.disabledMembers ?? [];
    return this.users.some(user => disabledMembers.some(id => String(id) === String(user.id)));
  }

  currentMembers(currentUser) {
    const disabledMembers = this.disabledMembers ?? [];
    const currentUsers = this.users.filter(
      user => !disabledMembers.some(id => String(id) === String(user.id))
    );
    return sortConversationMembers(currentUsers, currentUser);
  }

  previousMembers(currentUser) {
    const disabledMembers = this.disabledMembers ?? [];
    const previousUsers = this.users.filter(user =>
      disabledMembers.some(id => String(id) === String(user.id))
    );
    return sortConversationMembers(previousUsers, currentUser);
  }
}
